import { KernButton } from '@kern-ux-annex/kern-react-kit'
import { AnimatePresence, motion } from 'motion/react'
import { createContext, use, useEffect, useId, useRef, useState, type ReactNode } from 'react'

export function PageHeader({
  preline,
  title,
  children,
  actions,
}: {
  preline?: string
  title: string
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-16">
      <div className="flex max-w-[72ch] flex-col gap-4">
        {preline && <p className="kern-preline">{preline}</p>}
        <h1 className="kern-heading kern-heading-large">{title}</h1>
        {children && <div className="kern-body dms-muted">{children}</div>}
      </div>
      {actions && <div className="no-print flex flex-wrap gap-12">{actions}</div>}
    </header>
  )
}

export function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="flex flex-col gap-16">
      <div className="flex flex-wrap items-center justify-between gap-12">
        <h2 className="kern-heading kern-heading-medium">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  )
}

/**
 * Confirmation dialog on the native <dialog> element with KERN dialog styles
 * (focus trap and Escape handling come from the browser).
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  children: ReactNode
  confirmLabel: string
  danger?: boolean
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])
  return (
    <dialog ref={ref} className="kern-dialog" aria-labelledby={titleId} onClose={onClose}>
      <header className="kern-dialog__header">
        <h2 className="kern-title" id={titleId}>
          {title}
        </h2>
      </header>
      <section className="kern-dialog__body">{children}</section>
      <footer className="kern-dialog__footer">
        <KernButton variant="secondary" label="Abbrechen" onClick={onClose} />
        <KernButton
          variant={danger ? 'danger' : 'primary'}
          label={busy ? 'Bitte warten …' : confirmLabel}
          disabled={busy}
          onClick={onConfirm}
        />
      </footer>
    </dialog>
  )
}

/**
 * Modal with a form (create or edit something without leaving the page), on the native <dialog>
 * like ``ConfirmDialog``. The content mounts on opening, so every opening starts fresh; the form
 * brings its own buttons (action and "Abbrechen"/"Schließen").
 */
export function FormDialog({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean
  title: string
  children: ReactNode
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const locked = useRef(false)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal?.()
    if (!open && dialog.open) dialog.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      className="kern-dialog dms-form-dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      // Escape while a request runs would unmount the form and drop its result (e.g. a
      // one-time password); the form locks the dialog for that time (``useDialogLock``)
      onCancel={(e) => {
        if (locked.current) e.preventDefault()
      }}
    >
      <header className="kern-dialog__header">
        <h2 className="kern-title" id={titleId}>
          {title}
        </h2>
      </header>
      <DialogLock value={locked}>
        <section className="kern-dialog__body">{open && children}</section>
      </DialogLock>
    </dialog>
  )
}

const DialogLock = createContext<{ current: boolean } | null>(null)

/** Keeps the surrounding ``FormDialog`` open while ``busy`` (e.g. while a request runs). */
export function useDialogLock(busy: boolean): void {
  const lock = use(DialogLock)
  useEffect(() => {
    if (!lock) return
    lock.current = busy
    return () => {
      lock.current = false
    }
  }, [lock, busy])
}

/** Clickable step indicator for the guided workflow (ticket 07). */
/**
 * Steps inside one record (e.g. a measure). Deliberately looks different from the numbered process
 * navigation in the header: "Step 2 of 5" plus underlined tabs, so both levels are not confused.
 */
export function Stepper({
  steps,
  current,
  onSelect,
  done,
  disabled = false,
}: {
  steps: string[]
  current: number
  onSelect: (index: number) => void
  done: (index: number) => boolean
  /** e.g. while saving: no step change may start a second save */
  disabled?: boolean
}) {
  return (
    <nav aria-label="Schritte der Bearbeitung" className="no-print flex flex-col gap-8">
      <p className="kern-body kern-body--small dms-muted">
        Schritt {current + 1} von {steps.length}
      </p>
      <ol className="flex flex-wrap gap-x-4 border-b" style={{ borderColor: 'var(--dms-border)' }}>
        {steps.map((label, i) => {
          const active = i === current
          const complete = done(i) && !active
          return (
            <li key={label} className="relative">
              <button
                type="button"
                onClick={() => onSelect(i)}
                disabled={disabled}
                aria-current={active ? 'step' : undefined}
                className="dms-focus relative flex items-center gap-8 px-12 pt-8 pb-12 text-left text-kern-16"
                style={{ color: active ? 'var(--dms-action)' : 'var(--dms-text)', fontWeight: active ? 600 : 400 }}
              >
                {complete && (
                  <span
                    className="kern-icon kern-icon--check kern-icon--small"
                    style={{ backgroundColor: 'var(--kern-color-feedback-success)' }}
                    aria-hidden="true"
                  />
                )}
                <span>{label}</span>
                {complete && <span className="sr-only"> (erledigt)</span>}
                {active && (
                  <motion.span
                    layoutId="stepper-active"
                    className="absolute inset-x-0 -bottom-px h-4 rounded-full"
                    style={{ background: 'var(--dms-action)' }}
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * Slide transition between wizard steps; direction follows navigation. After a step change the
 * keyboard/screen-reader focus moves to the new step's heading (review finding), so users do
 * not stay on a button that no longer belongs to the visible content.
 */
export function StepTransition({ stepKey, direction, children }: { stepKey: string | number; direction: number; children: ReactNode }) {
  const container = useRef<HTMLDivElement>(null)
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    const timer = window.setTimeout(() => {
      const target = container.current?.querySelector<HTMLElement>('[data-step-focus], h2, legend, h3')
      if (!target) return
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true }) // scrolling belongs to the router (top of the new step)
    }, 260) // after the exit animation of the previous step
    return () => window.clearTimeout(timer)
  }, [stepKey])
  return (
    <div ref={container}>
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          key={stepKey}
          custom={direction}
          initial={{ opacity: 0, x: direction * 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -32 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

type Theme = 'auto' | 'light' | 'dark'

function applyTheme(theme: Theme) {
  if (theme === 'auto') document.documentElement.removeAttribute('data-kern-theme')
  else document.documentElement.setAttribute('data-kern-theme', theme)
}

/** Light / dark / system appearance (KERN dark mode via data-kern-theme). */
export function ThemeSwitch({ stacked = false }: { stacked?: boolean } = {}) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem('dms-theme') as Theme | null) ?? 'auto'
    } catch {
      return 'auto'
    }
  })
  useEffect(() => {
    applyTheme(theme)
    try {
      localStorage.setItem('dms-theme', theme)
    } catch {
      /* storage unavailable – keep in memory */
    }
  }, [theme])
  const options: { value: Theme; label: string }[] = [
    { value: 'auto', label: 'Automatisch' },
    { value: 'light', label: 'Hell' },
    { value: 'dark', label: 'Dunkel' },
  ]
  return (
    <div role="group" aria-label="Darstellung" className={`no-print flex gap-8 ${stacked ? 'flex-col items-start' : 'items-center'}`}>
      <span className="dms-muted text-kern-16">Darstellung</span>
      <div className="flex overflow-hidden rounded-kern-default border" style={{ borderColor: 'var(--dms-border)' }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={theme === o.value}
            onClick={() => setTheme(o.value)}
            className="dms-focus px-10 py-4 text-kern-16"
            style={
              theme === o.value
                ? { background: 'var(--dms-action)', color: 'var(--dms-on-action)' }
                : { background: 'var(--dms-surface)', color: 'var(--dms-text)' }
            }
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}
